import React from 'react';
import {
  BeakerIcon,
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { DailyFeedMix } from '../../api/nutrition';
import { useFormatting } from '../../hooks/useFormatting';

interface NutritionDailyMixesPanelProps {
  dailyMixes: DailyFeedMix[];
  selectedMixes: DailyFeedMix[];
  onSelectedMixesChange: (mixes: DailyFeedMix[]) => void;
  onView: (mix: DailyFeedMix) => void;
  onEdit: (mix: DailyFeedMix) => void;
  onDuplicate: (mix: DailyFeedMix) => void;
  onApprove: (mixId: string, approved: boolean) => void;
  onDelete: (mixId: string) => void;
}

export default function NutritionDailyMixesPanel({
  dailyMixes,
  selectedMixes,
  onSelectedMixesChange,
  onView,
  onEdit,
  onDuplicate,
  onApprove,
  onDelete,
}: NutritionDailyMixesPanelProps) {
  const { formatDate } = useFormatting();

  const dispenseLabel = (mix: DailyFeedMix) => {
    if (mix.metadata?.dispensedAt) return { text: 'Dispensed', className: 'bg-sky-100 text-sky-800', icon: TruckIcon };
    if (mix.metadata?.approved && mix.metadata?.readyForDispense !== false) {
      return { text: 'Ready for field', className: 'bg-cyan-100 text-cyan-800', icon: CheckCircleIcon };
    }
    if (mix.metadata?.approved) return { text: 'Approved', className: 'bg-emerald-100 text-emerald-800', icon: CheckCircleIcon };
    return { text: 'Pending approval', className: 'bg-amber-100 text-amber-800', icon: ClockIcon };
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-6 py-4">
        <h3 className="text-lg font-medium text-slate-900">Daily feed mixes</h3>
        <p className="mt-1 text-sm text-slate-600">Mixes for the selected date range and location</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-slate-50/80">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedMixes.length === dailyMixes.length && dailyMixes.length > 0}
                  onChange={(e) => onSelectedMixesChange(e.target.checked ? dailyMixes : [])}
                  className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Quantity</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Quality</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Field</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {dailyMixes.map((mix) => {
              const field = dispenseLabel(mix);
              const FieldIcon = field.icon;
              return (
              <tr key={mix.id} className="hover:bg-slate-50/80">
                <td className="whitespace-nowrap px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedMixes.some((s) => s.id === mix.id)}
                    onChange={(e) => {
                      if (e.target.checked) onSelectedMixesChange([...selectedMixes, mix]);
                      else onSelectedMixesChange(selectedMixes.filter((s) => s.id !== mix.id));
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                  {formatDate(new Date(mix.feedDate))}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-500">{mix.location || 'N/A'}</td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-900">
                  {(typeof mix.totalQuantity === 'number' ? mix.totalQuantity : parseFloat(String(mix.totalQuantity)) || 0).toFixed(1)} kg
                </td>
                <td className="whitespace-nowrap px-6 py-4">
                  <div className="flex items-center">
                    <div
                      className={`mr-2 h-2 w-2 rounded-full ${
                        mix.nutritionalSummary.qualityScore >= 80
                          ? 'bg-green-400'
                          : mix.nutritionalSummary.qualityScore >= 60
                            ? 'bg-yellow-400'
                            : 'bg-red-400'
                      }`}
                    />
                    <span className="text-sm text-slate-900">{mix.nutritionalSummary.qualityScore}/100</span>
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4">
                  {mix.metadata?.approved ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                      <CheckCircleIcon className="mr-1 h-3 w-3" />
                      Approved
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                      <ClockIcon className="mr-1 h-3 w-3" />
                      Pending
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-6 py-4">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${field.className}`}>
                    <FieldIcon className="mr-1 h-3 w-3" />
                    {field.text}
                  </span>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => onView(mix)} className="text-cyan-600 hover:text-cyan-800">View</button>
                    <button type="button" onClick={() => onEdit(mix)} className="text-cyan-600 hover:text-cyan-800">Edit</button>
                    <button type="button" onClick={() => onDuplicate(mix)} className="text-violet-600 hover:text-violet-800">Duplicate</button>
                    {!mix.metadata?.approved && (
                      <>
                        <button type="button" onClick={() => onApprove(mix.id, true)} className="text-green-600 hover:text-green-800">Approve</button>
                        <button type="button" onClick={() => onDelete(mix.id)} className="text-red-600 hover:text-red-800">Delete</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
        {dailyMixes.length === 0 && (
          <div className="py-12 text-center">
            <BeakerIcon className="mx-auto h-12 w-12 text-slate-400" />
            <h3 className="mt-2 text-sm font-medium text-slate-900">No feed mixes</h3>
            <p className="mt-1 text-sm text-slate-500">No mixes found for the selected filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}
